import React, { useEffect, useState } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaStore, FaPhone, FaEnvelope, FaMapMarkerAlt, FaBan, FaCheck, FaUser, FaClock, FaStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchStoreDetails, toggleStoreBan, toggleStoreStatus, clearError, clearSuccess, clearStoreDetails } from '../../../redux/Slices/AdminStoresSlice';
import ConfirmModal from '../../../shared/components/ConfirmModal';

const AdminStoreDetails = () => {
    const { setTitle } = useTitle();
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', data: null });
    
    const { storeDetails: store, detailsLoading: loading, updating, error, success } = useSelector((state) => state.adminStores);

    // دالة لتصحيح عرض رقم الهاتف
    const formatPhoneNumber = (phone) => {
        if (!phone) return 'غير محدد';
        
        // تحويل إلى نص للتأكد
        let phoneStr = String(phone).trim();
        
        // إزالة أي مسافات أو رموز غير مرغوب فيها
        phoneStr = phoneStr.replace(/\s+/g, '');
        
        // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
        if (phoneStr.startsWith('+')) {
            return phoneStr.slice(1) + '+';
        }
        
        // إذا كان الرقم ينتهي بـ + (مثل 201025376604+) - نتركه كما هو
        if (phoneStr.endsWith('+')) {
            return phoneStr;
        }
        
        // إذا كان الرقم بدون + (مثل 201025376604) - نضيف + في النهاية
        return phoneStr + '+';
    };

    useEffect(() => {
        setTitle('تفاصيل المتجر');
        dispatch(fetchStoreDetails(id));
        
        return () => {
            dispatch(clearStoreDetails());
        };
    }, [setTitle, id, dispatch]);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearError());
            if (!store) {
                navigate('/admin-stores');
            }
        }
    }, [error, dispatch, store, navigate]);

    useEffect(() => {
        if (success) {
            toast.success(success);
            dispatch(clearSuccess());
            dispatch(fetchStoreDetails(id));
        }
    }, [success, dispatch, id]);

    const handleToggleBan = async (banStatus) => {
        setConfirmModal({
            isOpen: true,
            type: 'ban',
            data: banStatus,
            message: banStatus === 1 ? 'هل أنت متأكد من حظر هذا المتجر؟' : 'هل أنت متأكد من إلغاء حظر هذا المتجر؟',
            title: banStatus === 1 ? 'تأكيد الحظر' : 'تأكيد إلغاء الحظر',
            confirmButtonColor: banStatus === 1 ? '#dc3545' : '#28a745'
        });
    };

    const handleToggleStatus = async () => {
        const currentStatus = store?.status;
        const newStatus = currentStatus === "1" || currentStatus === 1 ? 0 : 1;
        const confirmMessage = newStatus === 1 ? 'هل أنت متأكد من تفعيل هذا المتجر؟' : 'هل أنت متأكد من إلغاء تفعيل هذا المتجر؟';

        setConfirmModal({
            isOpen: true,
            type: 'status',
            data: newStatus,
            message: confirmMessage,
            title: newStatus === 1 ? 'تأكيد التفعيل' : 'تأكيد إلغاء التفعيل',
            confirmButtonColor: newStatus === 1 ? '#28a745' : '#ffc107'
        });
    };

    const handleConfirmAction = () => {
        if (confirmModal.type === 'ban') {
            dispatch(toggleStoreBan({ storeId: id, status: confirmModal.data }));
        } else if (confirmModal.type === 'status') {
            dispatch(toggleStoreStatus({ storeId: id, status: confirmModal.data }));
        }
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1) {
            return <span className="badge bg-success fs-6 text-white">نشط</span>;
        }
        return <span className="badge bg-danger fs-6 text-white">غير نشط</span>;
    };

    const getBanBadge = (ban) => {
        if (ban === "1" || ban === 1) {
            return <span className="badge bg-danger fs-6 text-white">محظور</span>;
        }
        return <span className="badge bg-success fs-6 text-white">غير محظور</span>;
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل المتجر...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!store) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <FaStore className="text-muted" style={{ fontSize: '3rem' }} />
                        <p className="mt-3 text-muted">المتجر غير موجود</p>
                        <button className="btn btn-primary" onClick={() => navigate('/admin-stores')}>
                            العودة للمتاجر
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/admin-stores')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة للمتاجر
                    </motion.button>
                    <div className="d-flex gap-2">
                        {/* Toggle status button */}
                        {store.status === "1" || store.status === 1 ? (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-warning btn-toggle d-flex align-items-center"
                                onClick={handleToggleStatus}
                                disabled={updating}>
                                {updating ? 'جاري...' : 'إلغاء التفعيل'}
                            </motion.button>
                        ) : (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-success btn-toggle d-flex align-items-center"
                                onClick={handleToggleStatus}
                                disabled={updating}>
                                {updating ? 'جاري...' : 'تفعيل المتجر'}
                            </motion.button>
                        )}
                        {store.ban === "1" || store.ban === 1 ? (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-success d-flex align-items-center"
                                onClick={() => handleToggleBan(0)}
                                disabled={updating}>
                                <FaCheck className="mx-2" />
                                {updating ? 'جاري...' : 'إلغاء الحظر'}
                            </motion.button>
                        ) : (
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-danger d-flex align-items-center"
                                onClick={() => handleToggleBan(1)}
                                disabled={updating}>
                                <FaBan className="mx-2" />
                                {updating ? 'جاري...' : 'حظر المتجر'}
                            </motion.button>
                        )}
                    </div>
                </div>

                {/* Store Details */}
                <div className="row">
                    {/* Basic Info */}
                    <div className="col-md-8">
                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="mb-0">المعلومات الأساسية</h5>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">اسم المتجر</label>
                                        <div className="d-flex align-items-center">
                                            <FaStore className="text-muted mx-2" />
                                            <span>{store.market_name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">اسم التاجر</label>
                                        <div className="d-flex align-items-center">
                                            <FaUser className="text-muted mx-2" />
                                            <span>{store.trader_name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">رقم الهاتف</label>
                                        <div className="d-flex align-items-center">
                                            <FaPhone className="text-muted mx-2" />
                                            <span>{formatPhoneNumber(store.phone)}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">رقم التواصل</label>
                                        <div className="d-flex align-items-center">
                                            <FaPhone className="text-muted mx-2" />
                                            <span>{formatPhoneNumber(store.contact_number)}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">التصنيف</label>
                                        <div className="d-flex align-items-center">
                                            <span>{store.category?.name || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-semibold">البريد الإلكتروني</label>
                                        <div className="d-flex align-items-center">
                                            <FaEnvelope className="text-muted mx-2" />
                                            <span>{store.email || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">وقت البدء</label>
                                        <div className="d-flex align-items-center">
                                            <FaClock className="text-muted mx-2" />
                                            <span>{store.start_time || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">وقت النهاية</label>
                                        <div className="d-flex align-items-center">
                                            <FaClock className="text-muted mx-2" />
                                            <span>{store.end_time || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">التقييم</label>
                                        <div className="d-flex align-items-center">
                                            <FaStar className="text-warning mx-2" />
                                            <span>{store.rate_avg || 'لا يوجد تقييم'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">حالة النشاط</label>
                                        <div className="d-flex align-items-center">
                                            {getStatusBadge(store.status)}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-semibold">حالة الحظر</label>
                                        <div className="d-flex align-items-center">
                                            {getBanBadge(store.ban)}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-semibold">العنوان</label>
                                        <div className="d-flex align-items-center">
                                            <FaMapMarkerAlt className="text-muted mx-2" />
                                            <span>{store.address || 'غير محدد'}</span>
                                        </div>
                                    </div>
                                    {store.description && (
                                        <div className="col-md-12 mb-3">
                                            <label className="form-label fw-semibold">الوصف</label>
                                            <div className="p-3 bg-light rounded">
                                                <p className="mb-0">{store.description}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Store Card */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">معلومات المتجر</h5>
                            </div>
                            <div className="card-body text-center">
                                <div className="mb-3">
                                    <div className="mx-auto mb-3" style={{ width: '120px', height: '120px' }}>
                                        {store.logo ? (
                                            <img
                                                src={store.logo}
                                                alt={store.market_name || 'متجر'}
                                                className="w-100 h-100 rounded"
                                                style={{ objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="bg-primary text-white d-flex align-items-center justify-content-center w-100 h-100 rounded">
                                                <FaStore style={{ fontSize: '3rem' }} />
                                            </div>
                                        )}
                                    </div>
                                    <h6>{store.market_name || 'غير محدد'}</h6>
                                    <p className="text-muted">{store.email || 'غير محدد'}</p>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="d-flex justify-content-around">
                                        <div>
                                            <small className="text-muted">حالة النشاط</small>
                                            <div className="mt-1">{getStatusBadge(store.status)}</div>
                                        </div>
                                        <div>
                                            <small className="text-muted">حالة الحظر</small>
                                            <div className="mt-1">{getBanBadge(store.ban)}</div>
                                        </div>
                                    </div>
                                </div>

                                
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, type: '', data: null })}
                onConfirm={handleConfirmAction}
                title={confirmModal.title || 'تأكيد العملية'}
                message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
                confirmText="تأكيد"
                cancelText="إلغاء"
                confirmButtonColor={confirmModal.confirmButtonColor || '#dc3545'}
                type={confirmModal.type === 'ban' && confirmModal.data === 1 ? 'danger' : 'warning'}
            />
        </div>
    );
};

export default AdminStoreDetails;
